import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
  TreatmentPlanFacade,
  TreatmentStepsFacade,
} from '@principle-theorem/ng-clinical-charting/store';
import { StafferSettingsStoreService } from '@principle-theorem/ng-principle-shared';
import {
  TrackByFunctions,
  type IMatSelectGroupOptions,
} from '@principle-theorem/ng-shared';
import { getAppointmentStepStatus } from '@principle-theorem/principle-core';
import {
  PlanStepPairStatus,
  type IAppointment,
  type IChartedMultiStepTreatmentStep,
  type IPatient,
  type ITreatmentPlan,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  getDoc,
  ISelectOption,
  isRefChanged$,
  isSameRef,
  isTimestamp,
  multiConcatMap,
  multiSwitchMap,
  reduce2DArray,
  sortTimestampAsc,
  type DocumentReference,
  type IGroup,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, type Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface ITreatmentPlanStepPair {
  treatmentPlan: WithRef<ITreatmentPlan>;
  treatmentStep: WithRef<ITreatmentStep>;
  appointment: WithRef<IAppointment> | undefined;
  status: PlanStepPairStatus;
}

export interface ITreatmentPlansConsolidatedState {
  loading: boolean;
  patient: WithRef<IPatient> | undefined;
  consolidatedPlan: ITreatmentPlanStepPair[];
  filteredConsolidatedPlan: IMatSelectGroupOptions<
    WithRef<ITreatmentPlan>,
    ITreatmentPlanStepPair
  >;
}

function initFilteredConsolidatedPlan(): IMatSelectGroupOptions<
  WithRef<ITreatmentPlan>,
  ITreatmentPlanStepPair
> {
  return {
    groups: [],
    trackByGroup:
      TrackByFunctions.ref<
        IGroup<ISelectOption<ITreatmentPlanStepPair>, WithRef<ITreatmentPlan>>
      >('group.ref'),
    trackByOption:
      TrackByFunctions.ref<ISelectOption<ITreatmentPlanStepPair>>(
        'treatmentStep.ref'
      ),
  };
}

const initialState: ITreatmentPlansConsolidatedState = {
  loading: false,
  patient: undefined,
  consolidatedPlan: [],
  filteredConsolidatedPlan: initFilteredConsolidatedPlan(),
};

@Injectable()
export class TreatmentPlansConsolidatedStore extends ComponentStore<ITreatmentPlansConsolidatedState> {
  private _treatmentPlanFacade = inject(TreatmentPlanFacade);
  private _treatmentStepsFacade = inject(TreatmentStepsFacade);
  private _stafferSettings = inject(StafferSettingsStoreService);
  readonly loading$ = this.select((store) => store.loading);
  readonly patient$ = this.select((store) => store.patient);
  readonly consolidatedPlan$ = this.select((store) => store.consolidatedPlan);
  readonly filteredConsolidatedPlan$ = this.select(
    (store) => store.filteredConsolidatedPlan
  );

  readonly initialiseStore = this.effect(
    (patient$: Observable<WithRef<IPatient>>) => {
      return patient$.pipe(
        isRefChanged$(),
        tap((patient) => {
          this.patchState({
            patient,
            loading: true,
          });
          this.loadConsolidatedPlan(this._treatmentPlanFacade.treatmentPlans$);
        })
      );
    }
  );

  readonly loadConsolidatedPlan = this.effect(
    (plans$: Observable<WithRef<ITreatmentPlan>[]>) => {
      return plans$.pipe(
        multiSwitchMap((plan) =>
          this._treatmentPlanFacade
            .getTreatmentSteps$(plan.ref)
            .pipe(
              multiConcatMap(async (step) =>
                this._buildConsolidatedPlan(plan, step)
              )
            )
        ),
        reduce2DArray(),
        map((consolidatedPlan) =>
          this._sortByAppointmentDateOrPlan(consolidatedPlan)
        ),
        tap((consolidatedPlan) => {
          this.patchState({
            consolidatedPlan,
            loading: false,
          });
        })
      );
    }
  );

  readonly updateFilteredSteps = this.effect(
    (consolidatedPlan$: Observable<ITreatmentPlanStepPair[]>) => {
      return combineLatest([
        consolidatedPlan$,
        this._stafferSettings.treatmentPlan$.pipe(
          map((settings) => settings.filters ?? [])
        ),
      ]).pipe(
        map(([consolidatedPlan, activeStatuses]) =>
          consolidatedPlan.filter(({ status }) =>
            activeStatuses.includes(status)
          )
        ),
        map((consolidatedPlan) => {
          const groupOptions = initFilteredConsolidatedPlan();

          for (const pair of consolidatedPlan) {
            const item = {
              label: pair.treatmentStep.name,
              value: pair,
            };

            const lastGroup =
              groupOptions.groups[groupOptions.groups.length - 1];
            const isSameAsLastPlan =
              lastGroup?.group.ref.path === pair.treatmentPlan.ref.path;

            if (isSameAsLastPlan) {
              lastGroup.items.push(item);
            } else {
              groupOptions.groups.push({
                group: pair.treatmentPlan,
                items: [item],
              });
            }
          }

          return groupOptions;
        }),
        tap((filteredConsolidatedPlan) =>
          this.patchState({ filteredConsolidatedPlan })
        )
      );
    }
  );

  constructor() {
    super(initialState);
    this.updateFilteredSteps(this.consolidatedPlan$);
  }

  async addStep(
    plan: WithRef<ITreatmentPlan>,
    afterStepRef?: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    await this._treatmentPlanFacade.addStep(plan, afterStepRef);
  }

  deleteStep(
    plan: WithRef<ITreatmentPlan>,
    step: WithRef<ITreatmentStep>
  ): void {
    this._treatmentPlanFacade.removeStep(plan, step);
  }

  updateStep(
    step: WithRef<ITreatmentStep>,
    change: Partial<IChartedMultiStepTreatmentStep | WithRef<ITreatmentStep>>
  ): void {
    this._treatmentStepsFacade.updateTreatmentStep(step.ref.id, change);
  }

  private async _buildConsolidatedPlan(
    treatmentPlan: WithRef<ITreatmentPlan>,
    treatmentStep: WithRef<ITreatmentStep>
  ): Promise<ITreatmentPlanStepPair> {
    const appointment = treatmentStep.appointment
      ? await getDoc(treatmentStep.appointment)
      : undefined;
    return {
      treatmentPlan,
      treatmentStep,
      appointment,
      status: getAppointmentStepStatus(treatmentStep, appointment),
    };
  }

  private _sortByAppointmentDateOrPlan(
    consolidatedPlan: ITreatmentPlanStepPair[]
  ): ITreatmentPlanStepPair[] {
    return consolidatedPlan.sort((planStepA, planStepB) => {
      const aDate = planStepA.appointment?.event?.from;
      const bDate = planStepB.appointment?.event?.from;

      // Both scheduled: sort by appointment date ascending
      if (isTimestamp(aDate) && isTimestamp(bDate)) {
        return sortTimestampAsc(aDate, bDate);
      }

      // Scheduled before unscheduled
      if (isTimestamp(aDate) && !isTimestamp(bDate)) {
        return -1;
      }
      if (!isTimestamp(aDate) && isTimestamp(bDate)) {
        return 1;
      }

      // Both unscheduled: group by plan, then sort by step index within plan
      const isSamePlan = isSameRef(
        planStepA.treatmentPlan.ref,
        planStepB.treatmentPlan.ref
      );
      if (isSamePlan) {
        const aIndex = planStepA.treatmentPlan.steps.findIndex((ref) =>
          isSameRef(ref, planStepA.treatmentStep.ref)
        );
        const bIndex = planStepB.treatmentPlan.steps.findIndex((ref) =>
          isSameRef(ref, planStepB.treatmentStep.ref)
        );
        return aIndex - bIndex;
      }

      // Different plans, both unscheduled: preserve original order
      return 0;
    });
  }
}
